import React, { useState, useCallback, useEffect } from 'react';
import { Header } from '../../components/layout';
import { Card, Pagination } from '../../components/common';
import {
  IdentityTable,
  IdentityFilters,
  IdentityDetail
} from '../../components/modules/identities';
import { StatsCard } from '../../components/modules/overview';
import { useIdentityContext } from '../../context/IdentityContext';
import { useIdentities } from '../../hooks/useIdentities';
import { useStats } from '../../hooks/useStats';
import { identitiesToCSV, downloadFile } from '../../utils/helpers';
import {
  FaUser,
  FaUserShield,
  FaUserFriends,
  FaClock
} from 'react-icons/fa';
import './Identities.css';

/**
 * HumanIdentities Page
 * Page for viewing human identities only
 */
const HumanIdentities = () => {
  const { stats, loading: statsLoading } = useStats();
  const {
    identities,
    pagination,
    loading,
    filters,
    updateFilters,
    setPage,
    setSearch,
    resetFilters
  } = useIdentities({ type: 'human' });

  const {
    selectedIdentity,
    isDetailModalOpen,
    openIdentityDetail,
    closeIdentityDetail
  } = useIdentityContext();

  const [searchValue, setSearchValue] = useState('');

  const handleSearch = useCallback((value) => {
    setSearchValue(value);
    setSearch(value);
  }, [setSearch]);

  const handleExport = useCallback(() => {
    if (identities && identities.length > 0) {
      const csv = identitiesToCSV(identities);
      const filename = `human-identities-export-${new Date().toISOString().split('T')[0]}.csv`;
      downloadFile(csv, filename);
    }
  }, [identities]);

  const humanStats = [
    {
      title: 'Total Users',
      value: stats?.human,
      icon: <FaUser />,
      iconColor: 'primary'
    },
    {
      title: 'Admin Users',
      value: stats?.adminUsers,
      icon: <FaUserShield />,
      iconColor: 'warning'
    },
    {
      title: 'Guest Users',
      value: stats?.guestUsers,
      icon: <FaUserFriends />,
      iconColor: 'info'
    },
    {
      title: 'Inactive Users',
      value: stats?.inactive,
      icon: <FaClock />,
      iconColor: 'secondary'
    }
  ];

  return (
    <div className="identities-page">
      <Header
        title="Human Identities"
        subtitle="View and manage user accounts"
        showSearch={true}
        showSync={true}
        showExport={true}
        searchValue={searchValue}
        onSearch={handleSearch}
        onExport={handleExport}
      />

      {/* Human-specific Stats */}
      <section className="human-stats" style={{ 
        display: 'grid', 
        gridTemplateColumns: `repeat(${Math.min(humanStats.length, 4)}, 1fr)`,
        gap: '20px',
        marginBottom: '24px'
      }}>
        {(Array.isArray(humanStats) ?? []).map((stat, index) => (
          <StatsCard
            key={index}
            title={stat?.title}
            value={stat?.value}
            icon={stat?.icon}
            iconColor={stat?.iconColor}
            loading={statsLoading}
          />
        ))}
      </section>

      {/* Identity List */}
      <Card padding={false}>
        <IdentityFilters
          filters={filters}
          onFilterChange={updateFilters}
          onReset={resetFilters}
          showTypeFilter={false}
        />
        
        <IdentityTable
          identities={identities}
          loading={loading}
          onRowClick={openIdentityDetail}
          onViewDetails={openIdentityDetail}
        />

        {pagination && (pagination?.totalPages ?? 1) > 1 && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={setPage}
          />
        )}
      </Card>

      {/* Identity Detail Modal */}
      <IdentityDetail
        identity={selectedIdentity}
        isOpen={isDetailModalOpen}
        onClose={closeIdentityDetail}
      />
    </div>
  );
};

export default HumanIdentities;